<template>
	<VerticalLayout class="pd-vertical">
		<Form :colon="false" :label-col="{ span: 12, lg: {span: 10 } }" :wrapper-col="{ span: 12, md: { span: 10 }, lg: { span: 8 } }">
			<FormItem :label="$t('baselines-edit.charts.qualityLabel')">
				<Select
					v-model:value="selectedQualityRef"
					:options="qualityOptions"
				/>
			</FormItem>
		</Form>
		<Spin :spinning="isStatsLoading">
			<VerticalLayout>
				<TypographyTitle :level="5">{{ $t('baselines-edit.charts.medians') }}</TypographyTitle>
				<Descriptions v-if="medians.length > 0" class="test-medians-table" size="small" :column="columnsCount" :bordered="true" :colon="false">
					<DescriptionsItem v-for="median in medians" :key="median.name" :label="median.name">{{ median.value }}</DescriptionsItem>
				</Descriptions>
				<Empty v-else :image="simpleImage" :description="$t('baselines-edit.charts.noData')" />
				<TypographyTitle class="pd-md-vertical" :level="5">{{ $t('baselines-edit.epochCounts.title') }}</TypographyTitle>
				<Descriptions class="test-epoch-counts-table" size="small" :column="columnsCount" :bordered="true" :colon="false">
					<DescriptionsItem :label="$t('baselines-edit.epochCounts.all')">{{ epochCounts.all }}</DescriptionsItem>
					<DescriptionsItem :label="$t('baselines-edit.epochCounts.selected')">{{ epochCounts.selected }}</DescriptionsItem>
					<DescriptionsItem :label="$t('baselines-edit.epochCounts.percent')">
						<NoLineBreaks>
							{{ epochCounts.percent }} %
						</NoLineBreaks>
					</DescriptionsItem>
				</Descriptions>
			</VerticalLayout>
		</Spin>
		<TimeSeriesChart :isLoading="isStatsLoading" :series="allSeries" :options="allOptions" />
	</VerticalLayout>
</template>

<script>
import { computed, toRef } from 'vue';
import { useVModel } from '@vueuse/core';

import { Descriptions, DescriptionsItem, Select, Form, FormItem, Spin, TypographyTitle, Empty } from 'ant-design-vue';

import { useSmallScreen } from '@/utils/useSmallScreen';

import VerticalLayout from '@/components/common/VerticalLayout.vue';
import TimeSeriesChart from '@/components/stats/TimeSeriesChart.vue';
import NoLineBreaks from '@/components/common/NoLineBreaks.vue';


export default {
	components: {
		Descriptions, DescriptionsItem, Select, Form, FormItem, Spin, TypographyTitle, Empty,
		VerticalLayout, TimeSeriesChart, NoLineBreaks,
	},
	props: {
		selectedQuality: {
			type: Number,
			required: true,
		},
		qualityOptions: {
			type: Array,
			required: true,
		},
		epochCounts: {
			type: Object,
			required: true,
		},
		allSeries: {
			type: Array,
			required: true,
		},
		allOptions: {
			type: Object,
			required: true,
		},
		isStatsLoading: {
			type: Boolean,
			required: true,
		},
	},
	setup(props, { emit }) {
		const selectedQualityRef = useVModel(props, 'selectedQuality', emit);
		const allSeriesRef = toRef(props, 'allSeries');

		const medians = computed(() => {
			return allSeriesRef.value.map(series => {
				return {
					name: series.name,
					value: series.median.toFixed(3),
				};
			});
		});

		const { smallScreen } = useSmallScreen();
		const columnsCount = computed(() => smallScreen.value ? 1 : 3);
		return {
			selectedQualityRef,
			medians,
			columnsCount,
			simpleImage: Empty.PRESENTED_IMAGE_SIMPLE,
		};
	},
};
</script> 

<style>
.baseline-neu-popup {
    background-color: white;
    width: 200px;
    overflow: hidden;
	font-family: 'Tahoma', sans-serif;
}

.baseline-neu-popup-header {
	background-color: #ECEFF1;
    padding: 6px 8px;
    border-bottom: 1px solid #e0e0e0;
}

.baseline-neu-datetime {
    color: #333;
    font-size: 14px;
}

.baseline-neu-popup-content {
    padding: 8px 8px;
}

.baseline-neu-coordinate-row {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
}

.baseline-neu-coordinate-row:last-child {
    margin-bottom: 0;
}

.baseline-neu-coordinate-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-right: 8px;
    flex-shrink: 0;
}

.baseline-neu-coordinate-label {
    color: #333;
    font-size: 14px;
    margin-right: auto;
}

.baseline-neu-coordinate-value {
    color: #333;
    font-size: 14px;
    font-weight: 600;
    text-align: right;
}
</style>